import { Box } from '@mui/material'
import React, { useEffect } from 'react'

import { useDebouncedFn } from '@/app/helpers'
import { CAMPAIGN_CHANGE_DELAY } from '@/app/module/campaigns/definitions'
import { ContentWhatsApp } from '@/app/module/campaigns/types'
import { getRegisterTemplateDisabledReason } from '@/app/module/campaigns/utils/whatsapp'
import WhatsappMessage from '@/app/module/common/whatsapp'
import { BaseWhatsAppContent, WhatsAppTab } from '@/app/module/common/whatsapp/helpers'
import { FileUploadType } from '@/app/types'

import { WhatsAppMessageType, WhatsappProps } from './whatsapp-props'
import WhatsappTemplateCheckbox from './whatsapp-template-checkbox'
import { hasButtons, hasList } from './whatsapp-utils'

type MediaContent =
  | {
      document: string
    }
  | {
      image: string
    }
  | {
      video: string
    }

type Props = WhatsappProps & {
  uploading: boolean
  onRecordingChange: (recording: boolean) => void
  onStop?: () => void
  shouldConfirm: (targetTab: WhatsAppTab, content: BaseWhatsAppContent) => boolean
}

const CampaignContentWhatsapp: React.FC<Props> = (props) => {
  const {
    changeDelay = CAMPAIGN_CHANGE_DELAY,
    item,
    tab,
    uploading,
    changeHandler: propChangeHandler,
    onTabChange,
  } = props

  const [mediaContent, setMediaContent] = React.useState<MediaContent | undefined>(undefined)

  const changeHandler = React.useCallback(
    (newItem: WhatsAppMessageType) => {
      if (!newItem.message.text || !newItem.message.video) {
        return propChangeHandler(newItem)
      }

      return propChangeHandler({
        ...newItem,
        message: {
          ...newItem.message,
          registerTemplate: true, // enforce register template if both video & caption are present
        },
      })
    },
    [propChangeHandler],
  )

  useEffect(() => {
    if (!hasList(item) || !item.message.registerTemplate) {
      return
    }

    changeHandler({
      ...item,
      message: {
        ...item.message,
        registerTemplate: false,
      },
    })
  }, [item, changeHandler])

  useEffect(() => {
    if (!mediaContent) {
      return
    }

    changeHandler({
      ...item,
      message: {
        ...item.message,
        ...mediaContent,
        audio: null,
      },
    })
    setMediaContent(undefined)
  }, [item, mediaContent, changeHandler])

  const saveHandler = async (file: FileUploadType) => {
    const { saveFileHandler } = props

    const res = await saveFileHandler(file)
    if (res instanceof Error || file.cancelled) {
      return
    }

    let uploadedContent: MediaContent | undefined

    if (file.type === 'video') {
      uploadedContent = { video: res.filename }
    } else if (file.type === 'image') {
      uploadedContent = { image: res.filename }
    } else if (file.type === 'document') {
      uploadedContent = { document: res.filename }
    }

    if (uploadedContent) {
      setMediaContent(uploadedContent)
    }
  }

  const removeMedia = () =>
    changeHandler({
      ...item,
      message: {
        ...item.message,
        image: '',
        audio: '',
        video: '',
        document: '',
      },
    })

  const hasWAButtons = hasButtons(item)
  const hasWAList = hasList(item)

  const itemRef = React.useRef<WhatsAppMessageType | null>(null)
  const changeHandlerRef = React.useRef(changeHandler)

  React.useEffect(
    () => () => {
      if (itemRef.current) {
        changeHandlerRef.current?.(itemRef.current)
      }
    },
    [],
  )

  const change = React.useCallback(
    (newItem: WhatsAppMessageType) => {
      changeHandler(newItem)
      itemRef.current = null
    },
    [changeHandler],
  )
  const debouncedHandleChange = useDebouncedFn(change, changeDelay)
  const handleChange = React.useCallback(
    (updatedValue: ContentWhatsApp) => {
      const newItem = {
        ...item,
        message: {
          ...item.message,
          ...updatedValue,
        },
      }
      itemRef.current = newItem
      debouncedHandleChange(newItem)
    },
    [item, debouncedHandleChange],
  )

  const disabledText = getRegisterTemplateDisabledReason(item)

  return (
    <div>
      <div>
        <WhatsappMessage
          {...props}
          {...props.item.message}
          autoFocus={props.item.autoFocus}
          canRegisterTemplate
          enableMediaCaption
          hasButtons={hasWAButtons}
          hasList={hasWAList}
          initialTab={tab}
          loading={props.loading || uploading}
          changeHandler={handleChange}
          onTabChange={onTabChange}
          removeMedia={removeMedia}
          saveHandler={saveHandler}
        />
        {!props.hideTemplateCheckbox && (
          <Box sx={{ marginTop: 2 }}>
            <WhatsappTemplateCheckbox
              disabledText={disabledText}
              registerTemplate={!!props.item.message?.registerTemplate}
              onChange={(value) =>
                changeHandler({
                  ...props.item,
                  message: {
                    ...props.item.message,
                    registerTemplate: value,
                  },
                })
              }
            />
          </Box>
        )}
      </div>
    </div>
  )
}

export default CampaignContentWhatsapp

export type { Props as CampaignContentWhatsappProps }
